import * as React from 'react';

import { useQuery } from '@apollo/client';

import config from 'js/app/config';
import path from 'js/lib/path';

import { urlConstants } from 'bundles/common/utils/urlUtils';
import GoogleSchemaCoursesQuery from 'bundles/seo/api/GoogleSchemaCoursesQuery.graphql';
import GoogleSchemaSpecializationsQuery from 'bundles/seo/api/GoogleSchemaSpecializationsQuery.graphql';
import type {
  GoogleSchemaCoursesQuery as GoogleSchemaCoursesData,
  GoogleSchemaCoursesQueryVariables,
} from 'bundles/seo/api/__generated__/GoogleSchemaCoursesQuery';
import type {
  GoogleSchemaSpecializationsQuery as GoogleSchemaSpecializationsData,
  GoogleSchemaSpecializationsQueryVariables,
} from 'bundles/seo/api/__generated__/GoogleSchemaSpecializationsQuery';
import ListGoogleSchemaMarkup from 'bundles/seo/components/ListGoogleSchemaMarkup';

export type Props = {
  courseIds: string[];
  specializationIds: string[];
};

const useCoursesData = (courseIds: string[]) => {
  const { data } = useQuery<GoogleSchemaCoursesData, GoogleSchemaCoursesQueryVariables>(GoogleSchemaCoursesQuery, {
    skip: courseIds?.length === 0,
    variables: {
      ids: courseIds,
    },
    errorPolicy: 'all',
    context: {
      clientName: 'gatewayGql',
    },
  });
  return data?.Course?.queryByIds;
};

const useSpecializationsData = (specializationIds: string[]) => {
  const { data } = useQuery<GoogleSchemaSpecializationsData, GoogleSchemaSpecializationsQueryVariables>(
    GoogleSchemaSpecializationsQuery,
    {
      skip: specializationIds?.length === 0,
      variables: {
        ids: specializationIds,
      },
      errorPolicy: 'all',
      context: {
        clientName: 'gatewayGql',
      },
    }
  );
  return data?.Specialization?.queryByIds;
};

const ListGoogleSchemaWithIds = ({ specializationIds, courseIds }: Props) => {
  const courses = useCoursesData(courseIds);
  const specializations = useSpecializationsData(specializationIds);

  const origin = config.url.domain;

  const courseUrls = courses?.map((item) => path.join(origin, urlConstants.courseRoot, item?.slug)) ?? [];

  const specializationUrls =
    specializations?.map((item) => path.join(origin, urlConstants.s12nsUrlRoot, item?.slug)) ?? [];

  const itemUrls = [...courseUrls, ...specializationUrls];

  return <ListGoogleSchemaMarkup itemUrls={itemUrls} />;
};

export default ListGoogleSchemaWithIds;
